import { ChangeEvent, Dispatch, SetStateAction, useCallback, useState } from 'react';
import { CsvParsedType, DataByEmployeeType, DataByWorkplaceType, ListType, SelectedOptionType, TriangulationDataFromApi } from '../../types';
import useApiCall from '../../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../../hooks/useGetGeneralAppData';
import TriangulationService from '../../../../services/TriangulationService';
import removeDuplicates from '../../../../utils/removeDuplicates';

interface UseFiltersInterface {
  setSindsList: Dispatch<SetStateAction<ListType>>;
  setEmployeesList: Dispatch<SetStateAction<ListType>>;
  setCurrentWorkplaceList: Dispatch<SetStateAction<ListType>>;
  setDestinyWorkplaceList: Dispatch<SetStateAction<ListType>>;
  dataList: CsvParsedType[];
  getTriangulationData: () => void;
  setVisualizationKind: Dispatch<SetStateAction<'cwp' | 'dwp' | 'employees'>>;
  setCsvFileName: Dispatch<SetStateAction<string>>;
  setCsvParsed: Dispatch<SetStateAction<CsvParsedType[]>>;
  setDataView: Dispatch<SetStateAction<'map' | 'list'>>;
  setRolesList: Dispatch<SetStateAction<ListType>>;
  setDataList: Dispatch<SetStateAction<CsvParsedType[]>>;
}

export default function useFilters({
  setSindsList,
  setEmployeesList,
  setCurrentWorkplaceList,
  setDestinyWorkplaceList,
  dataList,
  getTriangulationData,
  setVisualizationKind,
  setCsvFileName,
  setCsvParsed,
  setDataView,
  setRolesList,
  setDataList,
}: UseFiltersInterface) {
  const [isFiltering, setIsFiltering] = useState(false);
  const [isRolesSelectDisabled, setIsRolesSelectDisabled] = useState(false);
  const [selectedRole, setSelectedRole] = useState<SelectedOptionType>({ value: 'Todos', label: 'Todos' });
  const [selectedSind, setSelectedSind] = useState<SelectedOptionType>({ value: 'Todos', label: 'Todos' });
  const [selectedEmployee, setSelectedEmployee] = useState<SelectedOptionType>({ value: 0, label: 'Selecione um funcionário' });
  const [selectedCurrentWorkplace, setSelectedCurrentWorkplace] = useState<SelectedOptionType>({ value: 0, label: 'Selecione um Local de trabalho de origem (atual)' });
  const [selectedDestinyWorkplace, setSelectedDestinyWorkplace] = useState<SelectedOptionType>({ value: 0, label: 'Selecione um Local de trabalho de destino (indicado)' });

  const [dataByCurrentWorkplace, setDataByCurrentWorkplace] = useState<DataByWorkplaceType[]>([]);
  const [dataByDestinyWorkplace, setDataByDestinyWorkplace] = useState<DataByWorkplaceType[]>([]);
  const [dataByEmployee, setDataByEmployee] = useState<DataByEmployeeType[]>([]);

  const [rawData, setRawData] = useState<CsvParsedType[]>([]);

  const { apiCall } = useApiCall();
  const { userProfile, userCode, companyCode } = useGetGeneralAppData();

  const resetFields = useCallback((hasCompanyChanged: boolean) => {
    setVisualizationKind('cwp');
    setSelectedRole({ value: 'Todos', label: 'Todos' });
    setSelectedSind({ value: 'Todos', label: 'Todos' });
    setSelectedEmployee({ value: 0, label: 'Selecione um funcionário' });
    setSelectedCurrentWorkplace({ value: 0, label: 'Selecione um Local de trabalho de origem (atual)' });
    setSelectedDestinyWorkplace({ value: 0, label: 'Selecione um Local de trabalho de destino (indicado)' });
    setCsvFileName('');
    setCsvParsed([]);
    setDataByCurrentWorkplace([]);
    setDataByDestinyWorkplace([]);
    setDataByEmployee([]);
    setIsRolesSelectDisabled(false);
    setDataView('map');
    setRawData([]);

    if (hasCompanyChanged) {
      setRolesList([{ value: 'Todos', label: 'Todos' }]);
      setSindsList([{ value: 'Todos', label: 'Todos' }]);
      setEmployeesList([]);
      setCurrentWorkplaceList([]);
      setDestinyWorkplaceList([]);
      setDataList([]);
    }
  }, [setCsvFileName, setCsvParsed, setCurrentWorkplaceList, setDataList, setDataView, setDestinyWorkplaceList, setEmployeesList, setRolesList, setSindsList, setVisualizationKind]);

  const handleRoleChange = useCallback(async (event: ChangeEvent<HTMLSelectElement>) => {
    const choosedRole = event.target.value;
    if (selectedEmployee.value || selectedDestinyWorkplace.value || selectedCurrentWorkplace.value) {
      resetFields(false);
    }
    setSelectedRole({ value: choosedRole, label: choosedRole });
    if (selectedSind.value === 'Todos' && choosedRole !== 'Todos') {
      apiCall({
        apiToCall: TriangulationService.handleFiltersSelect,
        queryParams: { codEmpresa: companyCode, codUsu: userCode, perfilUsu: userProfile },
        reqBody: JSON.stringify({
          tipoSelecao: 1,
          descrCargo: choosedRole,
          codSindicato: 0,
          LTACod: null,
          LTICod: null,
          cpf: null,
        }),
        onStartLoad: () => setIsFiltering(true),
        onEndLoad: () => setIsFiltering(false),
        actionAfterResponse: (response: TriangulationDataFromApi) => {
          const newSindsList = response.listaSindicato.map((sind) => ({
            value: sind.codSindicato, label: String(sind.codSindicato),
          }));
          const newCurrentWorkplaceList = response.listaLTAtual.map((lta) => ({
            value: lta.LTACod, label: lta.LTADescr,
          }));
          const newDestinyWorkplaceList = response.listaLTIndicado.map((lti) => ({
            value: lti.LTICod, label: lti.LTIDescr,
          }));
          const newEmployeesList = response.listaFuncs.map((func) => ({
            value: func.cpf, label: func.nome,
          }));
          setSindsList(newSindsList);
          setCurrentWorkplaceList(newCurrentWorkplaceList);
          setDestinyWorkplaceList(newDestinyWorkplaceList);
          setEmployeesList(newEmployeesList);
        },
        catchMessage: 'Não foi possível realizar a pesquisa'
      });
    }
    if (selectedSind.value === 'Todos' && choosedRole === 'Todos') {
      getTriangulationData();
    }
  }, [apiCall, companyCode, getTriangulationData, resetFields, selectedCurrentWorkplace.value, selectedDestinyWorkplace.value, selectedEmployee.value, selectedSind.value, setCurrentWorkplaceList, setDestinyWorkplaceList, setEmployeesList, setSindsList, userCode, userProfile]);

  const handleSindChange = useCallback(async (event: ChangeEvent<HTMLSelectElement>) => {
    const choosedSind = event.target.value;
    if (selectedEmployee.value || selectedDestinyWorkplace.value || selectedCurrentWorkplace.value) {
      resetFields(false);
    }
    setSelectedSind({ value: choosedSind, label: choosedSind });
    if (selectedRole.value === 'Todos' && choosedSind === 'Todos') {
      setIsRolesSelectDisabled(false);
      getTriangulationData();
      return;
    }
    if (selectedRole.value === 'Todos' && choosedSind !== 'Todos') {
      await apiCall({
        apiToCall: TriangulationService.handleFiltersSelect,
        queryParams: { codEmpresa: companyCode, codUsu: userCode, perfilUsu: userProfile },
        reqBody: JSON.stringify({
          tipoSelecao: 2,
          descrCargo: null,
          codSindicato: choosedSind,
          LTACod: null,
          LTICod: null,
          cpf: null,
        }),
        onStartLoad: () => { setIsFiltering(true); setIsRolesSelectDisabled(true); },
        onEndLoad: () => setIsFiltering(false),
        actionAfterResponse: (response: TriangulationDataFromApi) => {
          const newCurrentWorkplaceList = response.listaLTAtual.map((lta) => ({
            value: lta.LTACod, label: lta.LTADescr,
          }));
          const newDestinyWorkplaceList = response.listaLTIndicado.map((lti) => ({
            value: lti.LTICod, label: lti.LTIDescr,
          }));
          const newEmployeesList = response.listaFuncs.map((func) => ({
            value: func.cpf, label: func.nome,
          }));
          setCurrentWorkplaceList(newCurrentWorkplaceList);
          setDestinyWorkplaceList(newDestinyWorkplaceList);
          setEmployeesList(newEmployeesList);
        },
      });
    }
    if (selectedRole.value !== 'Todos' && choosedSind === 'Todos') {
      await apiCall({
        apiToCall: TriangulationService.handleFiltersSelect,
        queryParams: { codEmpresa: companyCode, codUsu: userCode, perfilUsu: userProfile },
        reqBody: JSON.stringify({
          tipoSelecao: 1,
          descrCargo: selectedRole.value,
          codSindicato: 0,
          LTACod: null,
          LTICod: null,
          cpf: null,
        }),
        onStartLoad: () => { setIsFiltering(true); setIsRolesSelectDisabled(false); },
        onEndLoad: () => setIsFiltering(false),
        actionAfterResponse: (response: TriangulationDataFromApi) => {
          const newSindsList = response.listaSindicato.map((sind) => ({
            value: sind.codSindicato, label: String(sind.codSindicato),
          }));
          const newCurrentWorkplace = response.listaLTAtual.map((lta) => ({
            value: lta.LTACod, label: lta.LTADescr,
          }));
          const newDestinyWorkplaceList = response.listaLTIndicado.map((lti) => ({
            value: lti.LTICod, label: lti.LTIDescr,
          }));
          const newEmployeesList = response.listaFuncs.map((func) => ({
            value: func.cpf, label: func.nome,
          }));
          setSindsList(newSindsList);
          setCurrentWorkplaceList(newCurrentWorkplace);
          setDestinyWorkplaceList(newDestinyWorkplaceList);
          setEmployeesList(newEmployeesList);
        }
      });
      setIsRolesSelectDisabled(false);
    }
    if (selectedRole.value !== 'Todos' && choosedSind !== 'Todos') {
      await apiCall({
        apiToCall: TriangulationService.handleFiltersSelect,
        queryParams: { codEmpresa: companyCode, codUsu: userCode, perfilUsu: userProfile },
        reqBody: JSON.stringify({
          tipoSelecao: 3,
          descrCargo: selectedRole.value,
          codSindicato: choosedSind,
          LTACod: null,
          LTICod: null,
          cpf: null,
        }),
        onStartLoad: () => { setIsRolesSelectDisabled(true); setIsFiltering(true); },
        onEndLoad: () => setIsFiltering(false),
        actionAfterResponse: (response: TriangulationDataFromApi) => {
          const newCurrentWorkplaceList = response.listaLTAtual.map((lta) => ({
            value: lta.LTACod, label: lta.LTADescr,
          }));
          const newDestinyWorkplaceList = response.listaLTIndicado.map((lti) => ({
            value: lti.LTICod, label: lti.LTIDescr,
          }));
          const newEmployeesList = response.listaFuncs.map((func) => ({
            value: func.cpf, label: func.nome,
          }));
          setCurrentWorkplaceList(newCurrentWorkplaceList);
          setDestinyWorkplaceList(newDestinyWorkplaceList);
          setEmployeesList(newEmployeesList);
        }
      });
    }
  }, [apiCall, companyCode, getTriangulationData, resetFields, selectedCurrentWorkplace.value, selectedDestinyWorkplace.value, selectedEmployee.value, selectedRole.value, setCurrentWorkplaceList, setDestinyWorkplaceList, setEmployeesList, setSindsList, userCode, userProfile]);

  const handleCurrentWorkplaceChange = useCallback(async (opt: { value: number | string, label: string }) => {
    const choosedLTA = opt;
    setSelectedCurrentWorkplace({ value: Number(choosedLTA.value), label: choosedLTA.label });

    if (selectedSind.value !== 'Todos' && selectedRole.value !== 'Todos') {
      await apiCall({
        apiToCall: TriangulationService.handleFiltersSelect,
        queryParams: { codEmpresa: companyCode, codUsu: userCode, perfilUsu: userProfile },
        reqBody: JSON.stringify({
          tipoSelecao: 4,
          descrCargo: selectedRole.value,
          codSindicato: selectedSind.value,
          LTACod: choosedLTA.value,
          LTICod: null,
          cpf: null,
        }),
        onStartLoad: () => setIsFiltering(true),
        onEndLoad: () => setIsFiltering(false),
        actionAfterResponse: (response: TriangulationDataFromApi) => {
          const currentVT = response.dadosArq[0].valorVTA;
          const allIndicatedVts = response.dadosArq.map((i) => i.valorVTI);
          const minVT = allIndicatedVts.reduce((acc, cur) => (acc < cur ? acc : cur), currentVT);
          const employeesData = response.dadosArq.map((func) => ({
            name: func.nome,
            cpf: func.cpf,
            matricula: func.matricula,
            lat: func.funcLat,
            lng: func.funcLong,
            currentVT: func.valorVTA,
            minVT,
            locTrab: {
              name: func.LTADescr,
              lat: func.LTALat,
              lng: func.LTALong,
            },
          }));
          setRawData(response.dadosArq);
          setDataByCurrentWorkplace(removeDuplicates(employeesData));
        }
      });
    }
    if (selectedSind.value === 'Todos' && selectedRole.value !== 'Todos') {
      await apiCall({
        apiToCall: TriangulationService.handleFiltersSelect,
        queryParams: { codEmpresa: companyCode, codUsu: userCode, perfilUsu: userProfile },
        reqBody: JSON.stringify({
          tipoSelecao: 41,
          descrCargo: selectedRole.value,
          codSindicato: null,
          LTACod: choosedLTA.value,
          LTICod: null,
          cpf: null,
        }),
        onStartLoad: () => setIsFiltering(true),
        onEndLoad: () => setIsFiltering(false),
        actionAfterResponse: (response: TriangulationDataFromApi) => {
          const currentVT = response.dadosArq[0].valorVTA;
          const allIndicatedVts = response.dadosArq.map((i) => i.valorVTI);
          const minVT = allIndicatedVts.reduce((acc, cur) => (acc < cur ? acc : cur), currentVT);
          const employeesData = response.dadosArq.map((func) => ({
            name: func.nome,
            cpf: func.cpf,
            matricula: func.matricula,
            lat: func.funcLat,
            lng: func.funcLong,
            currentVT: func.valorVTA,
            minVT,
            locTrab: {
              name: func.LTADescr,
              lat: func.LTALat,
              lng: func.LTALong,
            },
          }));
          setRawData(response.dadosArq);
          setDataByCurrentWorkplace(removeDuplicates(employeesData));
        }
      });
    }
    if (selectedSind.value !== 'Todos' && selectedRole.value === 'Todos') {
      await apiCall({
        apiToCall: TriangulationService.handleFiltersSelect,
        queryParams: { codEmpresa: companyCode, codUsu: userCode, perfilUsu: userProfile },
        reqBody: JSON.stringify({
          tipoSelecao: 42,
          descrCargo: null,
          codSindicato: selectedSind.value,
          LTACod: choosedLTA.value,
          LTICod: null,
          cpf: null,
        }),
        onStartLoad: () => setIsFiltering(true),
        onEndLoad: () => setIsFiltering(false),
        actionAfterResponse: (response: TriangulationDataFromApi) => {
          const currentVT = response.dadosArq[0].valorVTA;
          const allIndicatedVts = response.dadosArq.map((i) => i.valorVTI);
          const minVT = allIndicatedVts.reduce((acc, cur) => (acc < cur ? acc : cur), currentVT);
          const employeesData = response.dadosArq.map((func) => ({
            name: func.nome,
            cpf: func.cpf,
            matricula: func.matricula,
            lat: func.funcLat,
            lng: func.funcLong,
            currentVT: func.valorVTA,
            minVT,
            locTrab: {
              name: func.LTADescr,
              lat: func.LTALat,
              lng: func.LTALong,
            },
          }));
          setRawData(response.dadosArq);
          setDataByCurrentWorkplace(removeDuplicates(employeesData));
        }
      });
    }
    if (selectedSind.value === 'Todos' && selectedRole.value === 'Todos') {
      const currentVT = dataList[0].valorVTA;
      const allIndicatedVts = dataList.map((i) => i.valorVTI);
      const minVT = allIndicatedVts.reduce((acc, cur) => (acc < cur ? acc : cur), currentVT);
      const filteredEmployees = dataList.filter((i) => i.LTACod === choosedLTA.value);
      const employeesData = filteredEmployees.map((func) => ({
        name: func.nome,
        cpf: func.cpf,
        matricula: func.matricula,
        lat: func.funcLat,
        lng: func.funcLong,
        currentVT: func.valorVTA,
        minVT,
        locTrab: {
          name: func.LTADescr,
          lat: func.LTALat,
          lng: func.LTALong,
        },
      }));
      setRawData(filteredEmployees);
      setDataByCurrentWorkplace(removeDuplicates(employeesData));
    }
  }, [apiCall, companyCode, dataList, selectedRole.value, selectedSind.value, userCode, userProfile]);

  const handleDestinyWorkplaceChange = useCallback(async (opt: { value: string | number, label: string }) => {
    const choosedLTI = opt;
    setSelectedDestinyWorkplace({ value: Number(choosedLTI.value), label: choosedLTI.label });

    if (selectedSind.value !== 'Todos' && selectedRole.value !== 'Todos') {
      await apiCall({
        apiToCall: TriangulationService.handleFiltersSelect,
        queryParams: { codEmpresa: companyCode, codUsu: userCode, perfilUsu: userProfile },
        reqBody: JSON.stringify({
          tipoSelecao: 5,
          descrCargo: selectedRole.value,
          codSindicato: selectedSind.value,
          LTACod: null,
          LTICod: choosedLTI.value,
          cpf: null,
        }),
        onStartLoad: () => setIsFiltering(true),
        onEndLoad: () => setIsFiltering(false),
        actionAfterResponse: (response: TriangulationDataFromApi) => {
          const employeesData = response.dadosArq.map((func) => ({
            name: func.nome,
            cpf: func.cpf,
            matricula: func.matricula,
            lat: func.funcLat,
            lng: func.funcLong,
            currentVT: func.valorVTA,
            indicatedVT: func.valorVTI,
            locTrab: {
              name: func.LTIDescr,
              lat: func.LTILat,
              lng: func.LTILong,
            },
          }));
          setRawData(response.dadosArq);
          setDataByDestinyWorkplace(removeDuplicates(employeesData));
        }
      });
    }
    if (selectedSind.value === 'Todos' && selectedRole.value !== 'Todos') {
      await apiCall({
        apiToCall: TriangulationService.handleFiltersSelect,
        queryParams: { codEmpresa: companyCode, codUsu: userCode, perfilUsu: userProfile },
        reqBody: JSON.stringify({
          tipoSelecao: 51,
          descrCargo: selectedRole.value,
          codSindicato: null,
          LTACod: null,
          LTICod: choosedLTI.value,
          cpf: null,
        }),
        onStartLoad: () => setIsFiltering(true),
        onEndLoad: () => setIsFiltering(false),
        actionAfterResponse: (response: TriangulationDataFromApi) => {
          const employeesData = response.dadosArq.map((func) => ({
            name: func.nome,
            cpf: func.cpf,
            matricula: func.matricula,
            lat: func.funcLat,
            lng: func.funcLong,
            currentVT: func.valorVTA,
            indicatedVT: func.valorVTI,
            locTrab: {
              name: func.LTIDescr,
              lat: func.LTILat,
              lng: func.LTILong,
            },
          }));
          setRawData(response.dadosArq);
          setDataByDestinyWorkplace(removeDuplicates(employeesData));
        }
      });
    }
    if (selectedSind.value !== 'Todos' && selectedRole.value === 'Todos') {
      await apiCall({
        apiToCall: TriangulationService.handleFiltersSelect,
        queryParams: { codEmpresa: companyCode, codUsu: userCode, perfilUsu: userProfile },
        reqBody: JSON.stringify({
          tipoSelecao: 52,
          descrCargo: null,
          codSindicato: selectedSind.value,
          LTACod: null,
          LTICod: choosedLTI.value,
          cpf: null,
        }),
        onStartLoad: () => setIsFiltering(true),
        onEndLoad: () => setIsFiltering(false),
        actionAfterResponse: (response: TriangulationDataFromApi) => {
          const employeesData = response.dadosArq.map((func) => ({
            name: func.nome,
            cpf: func.cpf,
            matricula: func.matricula,
            lat: func.funcLat,
            lng: func.funcLong,
            currentVT: func.valorVTA,
            indicatedVT: func.valorVTI,
            locTrab: {
              name: func.LTIDescr,
              lat: func.LTILat,
              lng: func.LTILong,
            },
          }));
          setRawData(response.dadosArq);
          setDataByDestinyWorkplace(removeDuplicates(employeesData));
        }
      });
    }
    if (selectedSind.value === 'Todos' && selectedRole.value === 'Todos') {
      const filteredEmployees = dataList.filter((i) => i.LTICod === Number(choosedLTI.value));
      const funcsData = filteredEmployees.map((func) => ({
        name: func.nome,
        cpf: func.cpf,
        matricula: func.matricula,
        lat: func.funcLat,
        lng: func.funcLong,
        currentVT: func.valorVTA,
        indicatedVT: func.valorVTI,
        locTrab: {
          name: func.LTIDescr,
          lat: func.LTILat,
          lng: func.LTILong,
        },
      }));
      setRawData(filteredEmployees);
      setDataByDestinyWorkplace(removeDuplicates(funcsData));
    }
  }, [apiCall, companyCode, dataList, selectedRole.value, selectedSind.value, userCode, userProfile]);

  const handleEmployeeChange = useCallback(async (opt: { value: string | number, label: string }) => {
    const choosedEmployee = opt;
    setSelectedEmployee({ value: choosedEmployee.value, label: choosedEmployee.label });
    await apiCall({
      apiToCall: TriangulationService.handleFiltersSelect,
      queryParams: { codEmpresa: companyCode, codUsu: userCode, perfilUsu: userProfile },
      reqBody: JSON.stringify({
        tipoSelecao: 6,
        descrCargo: null,
        codSindicato: null,
        LTACod: null,
        LTICod: null,
        cpf: choosedEmployee.value,
      }),
      onStartLoad: () => setIsFiltering(true),
      onEndLoad: () => setIsFiltering(false),
      actionAfterResponse: (response: TriangulationDataFromApi) => {
        const indicatedWorkplacesData = response.dadosArq.map((func) => ({
          employee: {
            name: func.nome,
            cpf: func.cpf,
            matricula: func.matricula,
            vt: func.valorVTA,
            lat: func.funcLat,
            lng: func.funcLong,
            currentLtLat: func.LTALat,
            currentLtLng: func.LTALong,
            currentLtName: func.LTADescr,
          },
          indicatedsLts: {
            id: func.LTICod,
            name: func.LTIDescr,
            vt: func.valorVTI,
            lat: func.LTILat,
            lng: func.LTILong,
          },
        }));
        setRawData(response.dadosArq);
        setDataByEmployee(indicatedWorkplacesData);
      },
      catchMessage: 'Ocorreu um erro ao realizar a pesquisa'
    });
  }, [apiCall, companyCode, userCode, userProfile]);

  return {
    isRolesSelectDisabled,
    selectedEmployee,
    selectedCurrentWorkplace,
    selectedDestinyWorkplace,
    dataByCurrentWorkplace,
    dataByDestinyWorkplace,
    dataByEmployee,
    rawData,
    handleRoleChange,
    handleSindChange,
    handleCurrentWorkplaceChange,
    handleDestinyWorkplaceChange,
    handleEmployeeChange,
    selectedRole,
    selectedSind,
    setSelectedDestinyWorkplace,
    setSelectedEmployee,
    setSelectedCurrentWorkplace,
    isFiltering,
    resetFields,
  };
}
